import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Newspaper, Plus, Edit, Trash2, Eye, EyeOff, Star, 
  Loader2, Calendar, Upload
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Categories for news
const NEWS_CATEGORIES = [
  { value: 'general', label: 'Général' },
  { value: 'market', label: 'Marchés' },
  { value: 'crypto', label: 'Crypto' },
  { value: 'investment', label: 'Investissement' },
  { value: 'company', label: 'Entreprise' },
];

export default function AdminNews() {
  const { token, user } = useAuth();
  const isOfficeAdmin = user?.office_id && ['admin_ops','bureau'].includes(user?.role);
  const [canEdit, setCanEdit] = useState(!isOfficeAdmin);
  const currentTemplate = useTheme().currentTemplate;
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [articleForm, setArticleForm] = useState({
    title: '',
    summary: '',
    content: '',
    cover_image: '',
    category: 'general',
    tags: '',
    is_published: true,
    is_featured: false
  });

  const primaryColor = currentTemplate?.colors?.primary || '#0052FF';

  useEffect(() => {
    fetchData();
    if (isOfficeAdmin) {
      axios.get(`${API}/offices/my-permissions`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setCanEdit(res.data?.can_manage_news ?? false)).catch(() => {});
    }
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/news/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArticles(response.data.articles || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const openNewArticle = () => {
    setEditingArticle(null);
    setArticleForm({
      title: '',
      summary: '',
      content: '',
      cover_image: '',
      category: 'general',
      tags: '',
      is_published: true,
      is_featured: false
    });
    setShowArticleModal(true);
  };

  const openEditArticle = (article) => {
    setEditingArticle(article);
    setArticleForm({
      title: article.title,
      summary: article.summary || '',
      content: article.content,
      cover_image: article.cover_image || '',
      category: article.category,
      tags: article.tags?.join(', ') || '',
      is_published: article.is_published,
      is_featured: article.is_featured
    });
    setShowArticleModal(true);
  };

  const handleSaveArticle = async () => {
    if (!articleForm.title || !articleForm.content) {
      toast.error('Le titre et le contenu sont obligatoires');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...articleForm,
        tags: articleForm.tags.split(',').map(t => t.trim()).filter(t => t)
      };

      if (editingArticle) {
        await axios.put(`${API}/news/${editingArticle.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Article mis à jour');
      } else {
        await axios.post(`${API}/news`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Article créé');
      }

      setShowArticleModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteArticle = async (articleId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet article ?')) return;
    
    try {
      await axios.delete(`${API}/news/${articleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Article supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleTogglePublish = async (article) => {
    try {
      await axios.put(`${API}/news/${article.id}`, {
        is_published: !article.is_published
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(article.is_published ? 'Article dépublié' : 'Article publié');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleToggleFeatured = async (article) => {
    try {
      await axios.put(`${API}/news/${article.id}`, {
        is_featured: !article.is_featured
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(article.is_featured ? 'Retiré de la une' : 'Mis à la une');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload-image`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      setArticleForm(prev => ({ ...prev, cover_image: fullUrl }));
      toast.success('Image uploadee');
    } catch (err) {
      toast.error('Erreur upload', { description: err.response?.data?.detail || 'Echec' });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Newspaper className="w-7 h-7" style={{ color: primaryColor }} />
            Gestion des Actualités
          </h1>
          <p className="text-muted-foreground mt-1">
            Créez et gérez les actualités pour vos clients
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-card/50 backdrop-blur border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/20">
                  <Newspaper className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total articles</p>
                  <p className="text-2xl font-bold text-white">{articles.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/50 backdrop-blur border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/20">
                  <Eye className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Publiés</p>
                  <p className="text-2xl font-bold text-white">
                    {articles.filter(a => a.is_published).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/50 backdrop-blur border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-yellow-500/20">
                  <Star className="w-5 h-5 text-yellow-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">À la une</p>
                  <p className="text-2xl font-bold text-white">
                    {articles.filter(a => a.is_featured).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/50 backdrop-blur border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/20">
                  <Eye className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total vues</p>
                  <p className="text-2xl font-bold text-white">
                    {articles.reduce((acc, a) => acc + (a.views || 0), 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Actions */}
        {canEdit && (
        <div className="flex justify-end">
          <Button onClick={openNewArticle} style={{ backgroundColor: primaryColor }}>
            <Plus className="w-4 h-4 mr-2" />
            Nouvel article
          </Button>
        </div>
        )}

        {/* Articles List */}
        <Card className="bg-card/50 backdrop-blur border-border/50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Articles</CardTitle>
              <div className="flex gap-1.5 flex-wrap">
                <Button size="sm" variant={filterCategory === 'all' ? 'default' : 'outline'} onClick={() => setFilterCategory('all')}>
                  Tous
                </Button>
                {NEWS_CATEGORIES.map(cat => (
                  <Button key={cat.value} size="sm" variant={filterCategory === cat.value ? 'default' : 'outline'} onClick={() => setFilterCategory(cat.value)}>
                    {cat.label}
                  </Button>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {articles.filter(a => filterCategory === 'all' || a.category === filterCategory).length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Newspaper className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Aucun article dans cette catégorie</p>
              </div>
            ) : (
              <div className="space-y-3">
                {articles.filter(a => filterCategory === 'all' || a.category === filterCategory).map((article) => (
                  <div 
                    key={article.id}
                    className="flex items-center justify-between p-4 rounded-lg border border-border bg-background/50 hover:border-primary/50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      {article.cover_image ? (
                        <img 
                          src={article.cover_image} 
                          alt=""
                          className="w-16 h-16 object-cover rounded"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-secondary rounded flex items-center justify-center">
                          <Newspaper className="w-6 h-6 text-muted-foreground" />
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium text-white">{article.title}</h3>
                          {article.is_featured && (
                            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <Badge variant="outline" className="text-xs">
                            {NEWS_CATEGORIES.find(c => c.value === article.category)?.label || article.category}
                          </Badge>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(article.created_at)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {article.views} vues
                          </span>
                          <Badge className={article.is_published ? 'bg-green-500/20 text-green-500' : 'bg-gray-500/20 text-gray-400'}>
                            {article.is_published ? 'Publié' : 'Brouillon'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleFeatured(article)}
                        className={article.is_featured ? 'text-yellow-500' : 'text-muted-foreground'}
                      >
                        <Star className={`w-4 h-4 ${article.is_featured ? 'fill-current' : ''}`} />
                      </Button>
                      {canEdit && (<>
                      <Button variant="ghost" size="sm" onClick={() => handleTogglePublish(article)}
                        className={article.is_published ? 'text-green-500' : 'text-muted-foreground'}>
                        {article.is_published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEditArticle(article)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteArticle(article.id)}
                        className="text-red-500 hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      </>)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Article Modal */}
        <Dialog open={showArticleModal} onOpenChange={setShowArticleModal}>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingArticle ? 'Modifier l\'article' : 'Nouvel article'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label>Titre *</Label>
                <Input
                  value={articleForm.title}
                  onChange={(e) => setArticleForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Titre de l'article"
                  className="bg-background/50"
                />
              </div>

              <div>
                <Label>Résumé</Label>
                <Textarea
                  value={articleForm.summary}
                  onChange={(e) => setArticleForm(prev => ({ ...prev, summary: e.target.value }))}
                  placeholder="Court résumé de l'article (affiché dans les aperçus)"
                  rows={2}
                  className="bg-background/50"
                />
              </div>

              <div>
                <Label>Contenu *</Label>
                <Textarea
                  value={articleForm.content}
                  onChange={(e) => setArticleForm(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Contenu de l'article (HTML supporté)"
                  rows={8}
                  className="bg-background/50 font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Vous pouvez utiliser des balises HTML : &lt;p&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;h2&gt;, &lt;h3&gt;, etc.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Catégorie</Label>
                  <Select 
                    value={articleForm.category} 
                    onValueChange={(v) => setArticleForm(prev => ({ ...prev, category: v }))}
                  >
                    <SelectTrigger className="bg-background/50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {NEWS_CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Tags</Label>
                  <Input
                    value={articleForm.tags}
                    onChange={(e) => setArticleForm(prev => ({ ...prev, tags: e.target.value }))}
                    placeholder="tag1, tag2, tag3"
                    className="bg-background/50"
                  />
                </div>
              </div>

              <div>
                <Label>Image de couverture</Label>
                <div className="flex items-start gap-4 mt-2">
                  <div className="flex-1 space-y-2">
                    <Input
                      value={articleForm.cover_image || ''}
                      onChange={(e) => setArticleForm(prev => ({ ...prev, cover_image: e.target.value }))}
                      placeholder="URL de l'image..."
                      className="bg-background/50"
                    />
                    <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-md bg-primary/10 hover:bg-primary/20 text-primary text-sm transition-colors">
                      <Upload className="w-4 h-4" />
                      Uploader une image
                      <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                    </label>
                  </div>
                  {articleForm.cover_image && (
                    <img 
                      src={articleForm.cover_image} 
                      alt="Preview"
                      className="w-20 h-20 object-cover rounded flex-shrink-0"
                    />
                  )}}
                </div>
              </div>

              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={articleForm.is_published}
                    onCheckedChange={(v) => setArticleForm(prev => ({ ...prev, is_published: v }))}
                  />
                  <Label>Publié</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={articleForm.is_featured}
                    onCheckedChange={(v) => setArticleForm(prev => ({ ...prev, is_featured: v }))}
                  />
                  <Label>À la une</Label>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowArticleModal(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleSaveArticle}
                disabled={saving}
                style={{ backgroundColor: primaryColor }}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingArticle ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
